if (!customElements.get('bulk-order-builder')) {
  customElements.define(
    'bulk-order-builder',
    class BulkOrderBuilder extends HTMLElement {
      constructor() {
        super();
        this.minQty = parseInt(this.dataset.minQty) || 25;
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.isActive = false;
      }

      connectedCallback() {
        this.config = JSON.parse(
          this.querySelector('[data-bob-config]').textContent
        );

        this.cacheElements();
        this.bindFormIntercept();
        this.bindQuantityWatcher();
        this.bindShipping();
        this.bindPostcard();
        this.bindNote();
        this.bindSummaryToggle();
        this.bindZoom();
        this.bindButtons();
        this.bindVariantChange();

        const qty = this.getQuantity();
        if (qty >= this.minQty) this.activate(qty);
      }

      disconnectedCallback() {
        if (this.zoomModal && this.zoomModal.parentNode === document.body) {
          document.body.removeChild(this.zoomModal);
        }
      }

      cacheElements() {
        this.productForm = document.getElementById(this.formId);
        this.productFormEl = this.productForm?.closest('product-form');
        this.quantityInput =
          this.productForm?.querySelector('.js-counter-quantity');
        this.originalSubmitBtn = document.getElementById(
          `ProductSubmitButton-${this.sectionId}`
        );
        this.originalButtonsWrapper = this.originalSubmitBtn?.closest(
          '.wt-product__add-to-cart_form--wrapper'
        );
        this.dynamicCheckout =
          this.productForm?.querySelector('.shopify-payment-button');
        this.stickyBuyBtn = document.querySelector(
          'sticky-buy-button.wt-product__sticky-buy'
        );

        this.shippingCheckboxes = this.querySelectorAll(
          'input[name="bob-shipping"]'
        );
        this.shippingInfo = this.querySelector('[data-bob-shipping-info]');
        this.singleLocationWrap = this.querySelector('[data-bob-single-location]');
        this.locationInput = this.querySelector('[data-bob-location-input]');

        this.postcardToggle = this.querySelector('[data-bob-postcard-toggle]');
        this.cardsSection = this.querySelector('[data-bob-cards-section]');
        this.cards = this.querySelectorAll('[data-bob-card]');

        this.noteToEl = this.querySelector('[data-bob-note-to]');
        this.noteMessageEl = this.querySelector('[data-bob-note-message]');
        this.noteFromEl = this.querySelector('[data-bob-note-from]');
        this.charCountEl = this.querySelector('[data-bob-char-count]');

        this.summaryEl = this.querySelector('[data-bob-summary]');
        this.summaryToggleBtn = this.querySelector('[data-bob-summary-toggle]');

        this.rowProduct = this.querySelector('[data-bob-row-product]');
        this.rowCard = this.querySelector('[data-bob-row-card]');
        this.rowShipping = this.querySelector('[data-bob-row-shipping]');
        this.rowDiscount = this.querySelector('[data-bob-row-discount]');
        this.rowTotal = this.querySelector('[data-bob-row-total]');

        this.addCartBtn = this.querySelector('[data-bob-add-cart]');
        this.buyNowBtn = this.querySelector('[data-bob-buy-now]');

        this.zoomModal = this.querySelector('[data-bob-zoom]');
        this.zoomImg = this.querySelector('[data-bob-zoom-img]');
        this.zoomCloseBtn = this.querySelector('[data-bob-zoom-close]');

        /* Move zoom modal to <body> so position:fixed is always viewport-relative */
        if (this.zoomModal) {
          document.body.appendChild(this.zoomModal);
        }
      }

      bindFormIntercept() {
        if (!this.productForm) return;
        this.productForm.addEventListener(
          'submit',
          (e) => {
            if (this.isActive) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.handleAddToCart(false);
            }
          },
          true
        );
      }

      getQuantity() {
        return parseInt(this.quantityInput?.value) || 1;
      }

      activate(qty) {
        this.isActive = true;
        this.classList.add('is-active');

        if (this.originalSubmitBtn) {
          this.originalSubmitBtn.style.display = 'none';
        }
        if (this.dynamicCheckout) {
          this.dynamicCheckout.style.display = 'none';
        }
        if (this.stickyBuyBtn) {
          this.stickyBuyBtn.style.display = 'none';
        }

        this.updateSummary(qty);
      }

      deactivate() {
        this.isActive = false;
        this.classList.remove('is-active');

        if (this.originalSubmitBtn) {
          this.originalSubmitBtn.style.display = '';
        }
        if (this.dynamicCheckout) {
          this.dynamicCheckout.style.display = '';
        }
        if (this.stickyBuyBtn) {
          this.stickyBuyBtn.style.display = '';
        }
      }

      /* ── Quantity watcher ── */
      bindQuantityWatcher() {
        if (!this.quantityInput) return;

        const handler = () => {
          const qty = this.getQuantity();
          if (qty >= this.minQty) {
            this.activate(qty);
          } else {
            this.deactivate();
          }
        };

        this.quantityInput.addEventListener('change', handler);
        this.quantityInput.addEventListener('input', handler);
      }

      /* ── Variant change listener ── */
      bindVariantChange() {
        if (!this.productForm) return;

        const variantInput = this.productForm.querySelector('input[name="id"]');
        if (!variantInput) return;

        const observer = new MutationObserver(() => {
          this.config.variantId = parseInt(variantInput.value);
          const variantData = this.getVariantPriceFromPage();
          if (variantData) {
            this.config.variantPrice = variantData.price;
            this.config.compareAtPrice = variantData.comparePrice || 0;
          }
          if (this.isActive) this.updateSummary(this.getQuantity());
        });

        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
        variantInput.addEventListener('change', () => {
          this.config.variantId = parseInt(variantInput.value);
          if (this.isActive) this.updateSummary(this.getQuantity());
        });
      }

      getVariantPriceFromPage() {
        const priceEl = document.querySelector(
          `#price-${this.sectionId} .price-item--regular, #price-${this.sectionId} .price-item--sale`
        );
        return null;
      }

      /* ── Shipping ── */
      bindShipping() {
        const updateShippingUI = () => {
          const checked = this.querySelector('input[name="bob-shipping"]:checked');
          if (!checked) return;
          const isSingle = checked.dataset.title?.toLowerCase().includes('single');
          if (this.singleLocationWrap) {
            this.singleLocationWrap.style.display = isSingle ? '' : 'none';
          }
          if (this.shippingInfo) {
            this.shippingInfo.style.display = isSingle ? 'none' : '';
          }
        };

        this.shippingCheckboxes.forEach((cb) => {
          cb.addEventListener('change', () => {
            this.shippingCheckboxes.forEach((other) => {
              if (other !== cb) other.checked = false;
            });
            if (!cb.checked) cb.checked = true;
            updateShippingUI();
            if (this.isActive) this.updateSummary(this.getQuantity());
          });
        });

        /* Set initial state based on pre-checked option */
        updateShippingUI();
      }

      getSelectedShipping() {
        const checked = this.querySelector(
          'input[name="bob-shipping"]:checked'
        );
        if (!checked) return null;
        return {
          variantId: parseInt(checked.value),
          price: parseInt(checked.dataset.price),
          title: checked.dataset.title,
        };
      }

      /* ── Postcard ── */
      bindPostcard() {
        if (this.postcardToggle) {
          this.postcardToggle.addEventListener('change', () => {
            if (this.postcardToggle.checked) {
              this.cardsSection?.classList.add('is-visible');
            } else {
              this.cardsSection?.classList.remove('is-visible');
            }
            if (this.isActive) this.updateSummary(this.getQuantity());
          });
        }

        this.cards.forEach((card) => {
          card.addEventListener('click', (e) => {
            if (e.target.closest('.bob__card-zoom')) return;
            this.cards.forEach((c) => c.classList.remove('is-selected'));
            card.classList.add('is-selected');
            if (this.isActive) this.updateSummary(this.getQuantity());
          });
        });
      }

      getSelectedPostcard() {
        if (!this.postcardToggle?.checked) return null;
        const selected = this.querySelector('[data-bob-card].is-selected');
        if (!selected) return null;
        const pc = this.config.postcardProduct;
        return {
          title: selected.dataset.title,
          variantId: pc ? pc.variantId : null,
          price: pc ? pc.price : 0,
        };
      }

      /* ── Note ── */
      bindNote() {
        if (this.noteMessageEl && this.charCountEl) {
          this.noteMessageEl.addEventListener('input', () => {
            const remaining =
              140 - this.noteMessageEl.value.length;
            this.charCountEl.textContent = remaining;
          });
        }
      }

      getNoteProperties() {
        const to = this.noteToEl?.value?.trim() || '';
        const message = this.noteMessageEl?.value?.trim() || '';
        const from = this.noteFromEl?.value?.trim() || '';
        if (!to && !message && !from) return null;
        return { to, message, from };
      }

      /* ── Summary toggle ── */
      bindSummaryToggle() {
        this.summaryToggleBtn?.addEventListener('click', () => {
          const isOpen = this.summaryEl.classList.toggle('is-open');
          this.summaryToggleBtn.setAttribute(
            'aria-expanded',
            isOpen.toString()
          );
        });
      }

      /* ── Zoom ── */
      bindZoom() {
        this.querySelectorAll('.bob__card-zoom').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const src = btn.dataset.zoomSrc;
            if (src && this.zoomImg && this.zoomModal) {
              this.zoomImg.src = src;
              this.zoomModal.classList.add('is-open');
              document.body.style.overflow = 'hidden';
            }
          });
        });

        const closeZoom = () => {
          this.zoomModal?.classList.remove('is-open');
          document.body.style.overflow = '';
        };

        this.zoomCloseBtn?.addEventListener('click', closeZoom);
        this.zoomModal?.addEventListener('click', (e) => {
          if (e.target === this.zoomModal) closeZoom();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') closeZoom();
        });
      }

      /* ── Payment summary calculation ── */
      formatMoney(paise) {
        const rupees = (paise / 100).toFixed(2);
        return (
          'Rs. ' + rupees.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        );
      }

      getDiscountPercent(qty) {
        const tiers = this.config.discountTiers || [];
        for (const tier of tiers) {
          if (qty >= tier.min && qty <= tier.max) return tier.percent;
        }
        return 0;
      }

      updateSummary(qty) {
        const productPrice = this.config.variantPrice;
        const productSubtotal = productPrice * qty;

        const productLabel =
          this.querySelector('[data-bob-row-product-label]');
        const productVal = this.querySelector('[data-bob-row-product-val]');
        if (productLabel)
          productLabel.textContent = `${this.config.productTitle} x ${qty}`;
        if (productVal)
          productVal.textContent = this.formatMoney(productSubtotal);
        if (this.rowProduct) this.rowProduct.style.display = '';

        let cardSubtotal = 0;
        const postcard = this.getSelectedPostcard();
        if (postcard && postcard.price > 0) {
          cardSubtotal = postcard.price * qty;
          const cardLabel = this.querySelector('[data-bob-row-card-label]');
          const cardVal = this.querySelector('[data-bob-row-card-val]');
          if (cardLabel)
            cardLabel.textContent = `Occasion Card (${postcard.title}) x ${qty}`;
          if (cardVal) cardVal.textContent = this.formatMoney(cardSubtotal);
          if (this.rowCard) this.rowCard.style.display = '';
        } else if (postcard) {
          const cardLabel = this.querySelector('[data-bob-row-card-label]');
          if (cardLabel)
            cardLabel.textContent = `Occasion Card: ${postcard.title}`;
          if (this.rowCard) {
            this.rowCard.style.display = '';
            const cardVal = this.querySelector('[data-bob-row-card-val]');
            if (cardVal) cardVal.textContent = 'Included';
          }
        } else {
          if (this.rowCard) this.rowCard.style.display = 'none';
        }

        let shippingSubtotal = 0;
        const shipping = this.getSelectedShipping();
        if (shipping) {
          shippingSubtotal = shipping.price * qty;
          const shipLabel = this.querySelector(
            '[data-bob-row-shipping-label]'
          );
          const shipVal = this.querySelector('[data-bob-row-shipping-val]');
          if (shipLabel)
            shipLabel.textContent = `Shipping to ${shipping.title} x ${qty}`;
          if (shipVal)
            shipVal.textContent = this.formatMoney(shippingSubtotal);
          if (this.rowShipping) this.rowShipping.style.display = '';
        } else {
          if (this.rowShipping) this.rowShipping.style.display = 'none';
        }

        const discountPercent = this.getDiscountPercent(qty);
        const discountAmount = Math.round(
          (productSubtotal * discountPercent) / 100
        );
        if (discountAmount > 0) {
          const discountVal = this.querySelector(
            '[data-bob-row-discount-val]'
          );
          if (discountVal)
            discountVal.textContent = `-${this.formatMoney(discountAmount)}`;
          if (this.rowDiscount) this.rowDiscount.style.display = '';
        } else {
          if (this.rowDiscount) this.rowDiscount.style.display = 'none';
        }

        const total =
          productSubtotal +
          cardSubtotal +
          shippingSubtotal -
          discountAmount;
        const totalVal = this.querySelector('[data-bob-row-total-val]');
        if (totalVal) totalVal.textContent = this.formatMoney(total);
      }

      /* ── Buttons ── */
      bindButtons() {
        this.addCartBtn?.addEventListener('click', () =>
          this.handleAddToCart(false)
        );
        this.buyNowBtn?.addEventListener('click', () =>
          this.handleAddToCart(true)
        );
      }

      async handleAddToCart(buyNow) {
        const qty = this.getQuantity();
        if (qty < this.minQty) return;

        const btn = buyNow ? this.buyNowBtn : this.addCartBtn;
        btn.classList.add('is-loading');
        btn.disabled = true;

        const items = [];
        const note = this.getNoteProperties();
        const properties = {};

        const shipping = this.getSelectedShipping();
        if (shipping) {
          properties['_Shipping Type'] = shipping.title;
          const isSingle = shipping.title?.toLowerCase().includes('single');
          if (isSingle && this.locationInput?.value.trim()) {
            properties['Delivery Address'] = this.locationInput.value.trim();
          }
        }

        const postcard = this.getSelectedPostcard();
        if (postcard) {
          properties['Occasion Card'] = postcard.title;
        }

        if (note) {
          if (note.to) properties['Note To'] = note.to;
          if (note.message) properties['Note Message'] = note.message;
          if (note.from) properties['Note From'] = note.from;
        }

        properties['_Bulk Order'] = 'true';

        const variantInput = this.productForm?.querySelector(
          'input[name="id"]'
        );
        const currentVariantId = variantInput
          ? parseInt(variantInput.value)
          : this.config.variantId;

        items.push({
          id: currentVariantId,
          quantity: qty,
          properties: properties,
        });

        if (shipping) {
          items.push({
            id: shipping.variantId,
            quantity: qty,
            properties: { '_Linked Product': this.config.productTitle },
          });
        }

        if (postcard && postcard.variantId) {
          items.push({
            id: postcard.variantId,
            quantity: qty,
            properties: {
              '_Linked Product': this.config.productTitle,
              'Design': postcard.title,
            },
          });
        }

        try {
          const cartDrawer = document.querySelector('cart-drawer');
          let sectionsParam = [];
          if (
            cartDrawer &&
            typeof cartDrawer.getSectionsToRender === 'function'
          ) {
            sectionsParam = cartDrawer
              .getSectionsToRender()
              .map((s) => s.id);
          }

          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: items,
              sections: sectionsParam,
              sections_url: window.location.pathname,
            }),
          });

          const data = await response.json();

          if (data.status && data.status !== 200) {
            alert(data.description || 'Could not add to cart. Please try again.');
            return;
          }

          if (buyNow) {
            if (typeof handleFloCheckoutBtn === 'function') {
              handleFloCheckoutBtn();
            } else {
              window.location.href = '/checkout';
            }
            return;
          }

          if (cartDrawer) {
            const isClosedCart = !document.body.classList.contains(
              'page-overlay-cart-on'
            );
            cartDrawer.renderContents(data, isClosedCart);
          } else {
            window.location.href = window.routes.cart_url;
          }

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bulk-order-builder',
              productVariantId: currentVariantId,
              cartData: data,
            });
          }
        } catch (error) {
          console.error('Bulk order add-to-cart error:', error);
          alert('Something went wrong. Please try again.');
        } finally {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      }
    }
  );
}
